import { createGenericStore } from '@/stores/utils/genericStore.js'
import axios from 'axios'
import { numericId, unwrapResource } from './chatStoreUtils.js'
import { useConversationStore } from './conversationStore.js'

const conversationParticipantStoreOptions = {
  state: () => ({
    isManagingParticipants: false,
    isMarkingRead: false,
    updatingWindowConversationIds: []
  }),

  actions: {
    patchConversationParticipantState(conversationId, participant) {
      useConversationStore().patchConversationParticipantState(conversationId, participant)
    },

    async markConversationRead(conversationId, payload = {}) {
      this.isMarkingRead = true
      this.error = null

      try {
        const response = await axios.patch(`/api/v1/conversations/${conversationId}/read`, payload)
        const participant = unwrapResource(response)
        this.patchConversationParticipantState(conversationId, participant)
        return participant
      } catch (error) {
        this.error = error
        throw error
      } finally {
        this.isMarkingRead = false
      }
    },

    async setChatWindowState(conversationId, state) {
      const normalizedConversationId = numericId(conversationId)

      this.updatingWindowConversationIds = [...new Set([...this.updatingWindowConversationIds, normalizedConversationId])]
      this.error = null

      try {
        const response = await axios.patch(`/api/v1/conversations/${normalizedConversationId}/window`, {
          state,
        })
        const participant = unwrapResource(response)
        this.patchConversationParticipantState(normalizedConversationId, participant)
        return participant
      } catch (error) {
        this.error = error
        throw error
      } finally {
        this.updatingWindowConversationIds = this.updatingWindowConversationIds.filter((id) => id !== normalizedConversationId)
      }
    },

    async addParticipants(conversationId, participantUserIds) {
      this.isManagingParticipants = true
      this.error = null

      try {
        const response = await axios.post(`/api/v1/conversations/${conversationId}/participants`, {
          participant_user_ids: participantUserIds
        })
        const participants = response.data?.data ?? []

        for (const participant of participants) {
          this.patchConversationParticipantState(conversationId, participant)
        }

        return participants
      } catch (error) {
        this.error = error
        throw error
      } finally {
        this.isManagingParticipants = false
      }
    },

    async removeParticipant(conversationId, userId) {
      this.isManagingParticipants = true
      this.error = null

      try {
        const response = await axios.delete(`/api/v1/conversations/${conversationId}/participants/${userId}`)
        const participant = unwrapResource(response)
        this.patchConversationParticipantState(conversationId, participant)
        return participant
      } catch (error) {
        this.error = error
        throw error
      } finally {
        this.isManagingParticipants = false
      }
    },

    async leaveConversation(conversationId) {
      this.isManagingParticipants = true
      this.error = null

      try {
        const response = await axios.post(`/api/v1/conversations/${conversationId}/leave`)
        const participant = unwrapResource(response)
        this.patchConversationParticipantState(conversationId, participant)
        useConversationStore().itemRemoveFromState(numericId(conversationId))
        return participant
      } catch (error) {
        this.error = error
        throw error
      } finally {
        this.isManagingParticipants = false
      }
    },

    async updateParticipantRole(conversationId, userId, role) {
      this.isManagingParticipants = true
      this.error = null

      try {
        const response = await axios.patch(`/api/v1/conversations/${conversationId}/participants/${userId}`, { role })
        const participant = unwrapResource(response)
        this.patchConversationParticipantState(conversationId, participant)
        return participant
      } catch (error) {
        this.error = error
        throw error
      } finally {
        this.isManagingParticipants = false
      }
    }
  }
}

export const useConversationParticipantStore = createGenericStore(
  'chat-conversation-participants',
  '/api/v1/conversations',
  conversationParticipantStoreOptions
)
